<template>
    <main class="solution-detail">
        <!-- ===== Hero Banner ===== -->
        <section class="hero" :style="heroStyle">
            <div class="hero-mask" aria-hidden="true"></div>

            <div class="hero-inner">
                <div class="hero-left">
                    <div class="hero-tag">应用行业</div>
                    <h1 class="hero-title">{{ page.heroTitle }}</h1>
                    <p class="hero-sub">{{ page.heroSubTitle }}</p>

                    <div class="hero-actions">
                        <a class="hero-btn primary" href="tel:13958349372">立即咨询</a>
                        <button class="hero-btn ghost" type="button" @click="$router.push('/AboutUs')">了解技明</button>
                    </div>
                </div>
            </div>
        </section>

        <!-- ===== Tabs（可 sticky） ===== -->
        <div ref="tabsWrapEl" class="tabs-wrap" :style="{ top: headerH + 'px' }">
            <div class="container">
                <div class="tabs">
                    <button v-for="t in tabs" :key="t.key" class="tab" :class="{ active: activeTab === t.key }"
                        type="button" @click="scrollTo(t.key)">
                        {{ t.label }}
                    </button>
                </div>
            </div>
        </div>

        <!-- ===== 场景痛点 ===== -->
        <section ref="secPainEl" class="section section-pain">
            <div class="container">
                <h3 class="section-title">场景痛点</h3>
                <p class="section-sub">
                    {{ page.painIntro }}
                </p>

                <div class="pain-grid">
                    <article v-for="p in page.pains" :key="p.title" class="pain-card">
                        <div class="pain-img">
                            <img v-if="p.img" :src="p.img" alt="" />
                            <div v-else class="img-ph">配图</div>
                        </div>

                        <div class="pain-body">
                            <div class="pain-title">{{ p.title }}</div>
                            <div class="pain-desc">{{ p.desc }}</div>
                        </div>
                    </article>
                </div>
            </div>
        </section>

        <!-- ===== 技明方案 ===== -->
        <section ref="secSolutionEl" class="section section-solution">
            <div class="container">
                <h3 class="section-title">技明方案</h3>

                <div class="sol-grid">
                    <div v-for="s in page.solutions" :key="s.title" class="sol-card">
                        <div class="sol-ico" aria-hidden="true">
                            <svg viewBox="0 0 24 24" class="sol-svg">
                                <path d="M4.5 6.5h15v11h-15z" fill="none" stroke="currentColor" stroke-width="1.6"
                                    stroke-linejoin="round" />
                                <path d="M7.5 10h9M7.5 13.5h6" fill="none" stroke="currentColor" stroke-width="1.6"
                                    stroke-linecap="round" />
                                <path d="M8 6.5V5.3c0-.7.6-1.3 1.3-1.3h5.4c.7 0 1.3.6 1.3 1.3v1.2" fill="none"
                                    stroke="currentColor" stroke-width="1.6" />
                            </svg>
                        </div>

                        <div class="sol-main">
                            <div class="sol-title">{{ s.title }}</div>
                            <div class="sol-desc">{{ s.desc }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ===== 合作方式 ===== -->
        <section ref="secCaseEl" class="section section-case">
            <div class="container">
                <h3 class="section-title">合作方式</h3>

                <div class="case-grid">
                    <div v-for="c in page.cases" :key="c.name" class="case-item">
                        <div class="case-logo">
                            <div class="logo-ph">{{ c.short || c.name }}</div>
                        </div>
                        <div class="case-name">{{ c.name }}</div>
                        <div class="case-desc">{{ c.desc }}</div>
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

/** 顶部导航高度（sticky tabs 偏移量） */
const headerH = 76;

/** 读取 query 参数：?key=line / auto / logistics ... */
const route = useRoute();
const key = computed(() => String(route.query.key || "line"));

const solutionMap = {
    line: {
        heroTitle: "工业产线应用",
        heroSubTitle: "精准定位辅助装配作业，提升产线效率与一致性",
        heroBg: require("../static/1.webp"),

        painIntro: "产线装配对定位精度与作业效率要求高，人工对位易出错，多工位标准难以统一",
        pains: [
            {
                title: "人工对位误差大",
                desc: "精密装配、元件焊接等工位依赖人工目测对位，误差积累影响成品一致性。",
                img: require("../static/1.webp"),
            },
            {
                title: "多工位标准难统一",
                desc: "不同工位、不同批次的标准难以保持一致，返工与换线成本高。",
                img: require("../static/2.webp"),
            },
            {
                title: "效率提升遇瓶颈",
                desc: "传统对位方式耗时费力，制约产线节拍，难以满足快速换产需求。",
                img: require("../static/4.webp"),
            },
        ],

        solutions: [
            { title: "毫米级精准定位", desc: "激光定位灯为工位提供清晰一致的定位基准，降低对位误差，提升作业准确率。" },
            { title: "多工位标准统一", desc: "统一的定位标准贯穿各工位，减少返工与换线调试时间，提升整体节拍。" },
            { title: "灵活定制适配", desc: "按产线实际布局定制投射图形与安装方式，灵活适配不同工艺要求。" },
            { title: "稳定耐用设计", desc: "高强度结构设计，抗震动、耐粉尘，适应连续生产环境长期使用。" },
        ],

        cases: [
            { name: "产品定制", short: "定制", desc: "按需求定制定位灯与配套产品" },
            { name: "批量生产", short: "量产", desc: "稳定产能，支持批量制造加工" },
            { name: "协同配套", short: "配套", desc: "家电/照明/塑料/五金协同供货" },
            { name: "进出口对接", short: "外贸", desc: "自营+代理，助力出口合作" },
        ],
    },

    auto: {
        heroTitle: "汽车制造应用",
        heroSubTitle: "为汽车零部件加工与装配提供可靠定位与配套支持",
        heroBg: require("../static/2.webp"),

        painIntro: "汽车零部件加工环境复杂，对定位精度、耐环境性与连续作业稳定性要求严苛",
        pains: [
            {
                title: "车间工况复杂",
                desc: "高温、粉尘、油污等复杂工况对定位设备稳定性提出更高要求。",
                img: require("../static/2.webp"),
            },
            {
                title: "精度要求严苛",
                desc: "内外饰件、零部件装配对位置精度敏感，微小偏差即影响装配质量。",
                img: require("../static/6.webp"),
            },
            {
                title: "连续生产压力大",
                desc: "批量、连续的生产节奏要求设备长时间稳定运行，减少停机维护。",
                img: require("../static/4.webp"),
            },
        ],

        solutions: [
            { title: "耐环境设计", desc: "抗高温、粉尘与油污的防护设计，适应车间复杂工况长期作业。" },
            { title: "高精度定位", desc: "清晰稳定的定位指示，助力零部件加工与装配精度提升。" },
            { title: "高强度结构", desc: "高强度铝合金外壳与抗振设计，满足连续生产与震动环境使用。" },
            { title: "零部件协同配套", desc: "依托五金、塑料等自制能力，提供零部件加工协同配套。" },
        ],

        cases: [
            { name: "零部件加工", short: "加工", desc: "五金、塑料件等零部件配套加工" },
            { name: "装配定位", short: "装配", desc: "定位灯辅助装配工序精准作业" },
            { name: "模具制造", short: "模具", desc: "配合注塑模具与成型工艺支持" },
            { name: "长期供应", short: "供应", desc: "稳定产能保障长期批量供应" },
        ],
    },

    logistics: {
        heroTitle: "仓储物流应用",
        heroSubTitle: "清晰视觉指引，提升分拣、搬运与作业效率",
        heroBg: require("../static/4.webp"),

        painIntro: "仓储分拣作业量大、货位众多，错拣漏拣影响效率，夜间与强光环境对指引清晰度要求高",
        pains: [
            {
                title: "错拣漏拣频发",
                desc: "货架货位指引不清，分拣人员易错拿、漏拿，影响发货准确率。",
                img: require("../static/1.webp"),
            },
            {
                title: "夜间作业困难",
                desc: "夜间或昏暗环境中货位标识难以辨识，作业效率与安全性下降。",
                img: require("../static/2.webp"),
            },
            {
                title: "设备协同复杂",
                desc: "AGV、输送线等设备协同要求高，缺乏统一清晰的作业指引。",
                img: require("../static/4.webp"),
            },
        ],

        solutions: [
            { title: "货位视觉指引", desc: "货位、分拣口提供清晰视觉指引，降低错拣、漏拣概率。" },
            { title: "昼夜连续作业", desc: "强光与昏暗环境下均清晰可见，支持昼夜连续作业。" },
            { title: "设备协同优化", desc: "与 AGV、输送线等设备协同，优化场内搬运与分拣流程。" },
            { title: "场景定制方案", desc: "按仓储布局定制指引方案，灵活适配不同场地与流程。" },
        ],

        cases: [
            { name: "仓储指引", short: "仓储", desc: "货架货位视觉指引方案" },
            { name: "分拣支持", short: "分拣", desc: "分拣口定位与作业引导" },
            { name: "智能仓配", short: "智能", desc: "配合 AGV 等智能设备作业" },
            { name: "出口配套", short: "外贸", desc: "仓储设备及配套产品出口" },
        ],
    },

    machining: {
        heroTitle: "机械加工应用",
        heroSubTitle: "抗震耐油污的定位方案，辅助设备运维与标准作业",
        heroBg: require("../static/6.webp"),

        painIntro: "机械加工环境振动频繁、油污粉尘多，检修点位与管道铺设需要清晰稳定的定位标记",
        pains: [
            {
                title: "振动环境定位难",
                desc: "机床、产线高频振动导致普通定位设备偏移，影响标记稳定性。",
                img: require("../static/1.webp"),
            },
            {
                title: "油污粉尘干扰",
                desc: "油污、粉尘附着影响标识清晰度，维护频次高。",
                img: require("../static/2.webp"),
            },
            {
                title: "检修点位不直观",
                desc: "大型设备检修点位、管道走向缺乏直观标记，作业效率低。",
                img: require("../static/4.webp"),
            },
        ],

        solutions: [
            { title: "抗振设计", desc: "抗强震结构设计，适应机床、产线等高频振动环境稳定工作。" },
            { title: "耐油污材质", desc: "耐油污、防腐蚀表面处理，减少清洁维护频次。" },
            { title: "定制投射图案", desc: "按需定制投射图形，辅助标准化、目视化作业。" },
            { title: "全套定位设备", desc: "工业定位灯等设备制造能力，提供全套定位装备支持。" },
        ],

        cases: [
            { name: "设备检修", short: "检修", desc: "检修点位定位标记方案" },
            { name: "管道工程", short: "管道", desc: "管道走向与施工定位" },
            { name: "目视化作业", short: "目视", desc: "车间标准化目视管理" },
            { name: "设备出口", short: "出口", desc: "定位设备外贸出口合作" },
        ],
    },

    electronic: {
        heroTitle: "电子电器应用",
        heroSubTitle: "覆盖家电与电子制造的装配定位、目视化与协同配套",
        heroBg: require("../static/1.webp"),

        painIntro: "电子电器制造品类多、工序细，装配定位与质量目视化需求并存，多品类协同配套要求高",
        pains: [
            {
                title: "装配定位精度要求高",
                desc: "电子元件、线束装配点位多且细小，对定位指引的清晰度要求高。",
                img: require("../static/2.webp"),
            },
            {
                title: "质量检查依赖经验",
                desc: "工序质量检查缺少标准化目视引导，新人上手慢、漏检风险高。",
                img: require("../static/6.webp"),
            },
            {
                title: "多品类配套复杂",
                desc: "整机生产涉及注塑、五金、电器件等多品类协同，供应链整合难。",
                img: require("../static/4.webp"),
            },
        ],

        solutions: [
            { title: "装配定位指引", desc: "为装配、焊接、检测工位提供清晰定位指引，提升作业一致性。" },
            { title: "目视化质量检查", desc: "标准化的视觉引导辅助质检点位确认，降低漏检风险。" },
            { title: "多品类协同配套", desc: "注塑、五金、电器制造协同，提供零部件到整机的配套支持。" },
            { title: "定制生产服务", desc: "支持小批量试产与大批量生产灵活切换，满足不同订单需求。" },
        ],

        cases: [
            { name: "小家电制造", short: "家电", desc: "小家电整机与配件制造" },
            { name: "电子装配", short: "电子", desc: "电子元器件装配配套" },
            { name: "线束加工", short: "线束", desc: "线束、连接件加工支持" },
            { name: "整机代工", short: "代工", desc: "OEM / ODM 整机代工" },
        ],
    },

    lighting: {
        heroTitle: "照明工程应用",
        heroSubTitle: "依托灯具制造能力，提供工业特种照明与工程配套",
        heroBg: require("../static/2.webp"),

        painIntro: "照明工程对产品稳定性、定制化与场景适配要求高，工业特种照明需求日益多元化",
        pains: [
            {
                title: "工程照明需求多样",
                desc: "厂房、道路、工程现场照明场景差异大，单一产品难以覆盖。",
                img: require("../static/4.webp"),
            },
            {
                title: "特种照明门槛高",
                desc: "防爆、耐高温等特种照明对安全与可靠性要求严苛。",
                img: require("../static/1.webp"),
            },
            {
                title: "定制响应周期长",
                desc: "工程项目的定制需求沟通链路长，交付周期难保障。",
                img: require("../static/2.webp"),
            },
        ],

        solutions: [
            { title: "灯具制造能力", desc: "依托灯具制造经验，提供工业特种照明与工程照明产品。" },
            { title: "定位照明产品", desc: "工业定位灯等产品应用于工程定位与指示场景。" },
            { title: "项目定制服务", desc: "按项目需求定制产品，提供从设计到应用的一体化服务。" },
            { title: "稳定交付保障", desc: "成熟产线保证交期，配合进出口业务支持海外项目。" },
        ],

        cases: [
            { name: "工程照明", short: "工程", desc: "工程类灯具定制与供货" },
            { name: "特种照明", short: "特种", desc: "防爆、耐高温等特种灯具" },
            { name: "定制灯具", short: "定制", desc: "按场景需求定制产品" },
            { name: "海外项目", short: "海外", desc: "灯具产品外贸出口合作" },
        ],
    },
};

const page = computed(() => solutionMap[key.value] || solutionMap.line);

const heroStyle = computed(() => ({
    backgroundImage: `url(${page.value.heroBg})`,
}));

/** Tabs + 滚动联动 */
const tabs = [
    { key: "pain", label: "场景痛点" },
    { key: "solution", label: "技明方案" },
    { key: "case", label: "合作方式" },
];

const activeTab = ref("pain");

const tabsWrapEl = ref(null);
const secPainEl = ref(null);
const secSolutionEl = ref(null);
const secCaseEl = ref(null);

const tabsH = ref(56);

function calcTabsHeight() {
    tabsH.value = tabsWrapEl.value?.offsetHeight || 56;
}

function scrollTo(tabKey) {
    const map = {
        pain: secPainEl,
        solution: secSolutionEl,
        case: secCaseEl,
    };
    const el = map[tabKey]?.value;
    if (!el) return;

    const top =
        el.getBoundingClientRect().top +
        window.scrollY -
        headerH -
        tabsH.value +
        1;

    window.scrollTo({ top, behavior: "smooth" });
}

let io;
function setupObserver() {
    if (io) io.disconnect();

    io = new IntersectionObserver(
        (entries) => {
            const visible = entries
                .filter((e) => e.isIntersecting)
                .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

            if (!visible) return;

            if (visible.target === secPainEl.value) activeTab.value = "pain";
            if (visible.target === secSolutionEl.value) activeTab.value = "solution";
            if (visible.target === secCaseEl.value) activeTab.value = "case";
        },
        {
            threshold: [0.2, 0.35, 0.5, 0.65],
            rootMargin: `-${headerH + tabsH.value + 10}px 0px -55% 0px`,
        }
    );

    if (secPainEl.value) io.observe(secPainEl.value);
    if (secSolutionEl.value) io.observe(secSolutionEl.value);
    if (secCaseEl.value) io.observe(secCaseEl.value);
}

onMounted(async () => {
    await nextTick();
    calcTabsHeight();
    setupObserver();
    window.addEventListener("resize", onResize, { passive: true });
});

function onResize() {
    calcTabsHeight();
    setupObserver();
}

onBeforeUnmount(() => {
    if (io) io.disconnect();
    window.removeEventListener("resize", onResize);
});

watch(
    () => key.value,
    async () => {
        await nextTick();
        calcTabsHeight();
        setupObserver();
        window.scrollTo({ top: 0, behavior: "smooth" });
    }
);
</script>

<style scoped>
/* ===== Page ===== */
.solution-detail {
    background: #fff;
}

/* ===== Hero ===== */
.hero {
    position: relative;
    min-height: 380px;
    background-size: cover;
    background-position: center;
    overflow: hidden;
}

.hero-mask {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg,
            rgba(6, 38, 78, 0.82) 0%,
            rgba(8, 58, 116, 0.6) 50%,
            rgba(10, 79, 151, 0.25) 100%);
}

.hero-inner {
    position: relative;
    max-width: 1280px;
    margin: 0 auto;
    padding: 96px 24px 84px;
    display: flex;
    align-items: center;
}

.hero-left {
    color: #fff;
    max-width: 760px;
}

.hero-tag {
    display: inline-block;
    padding: 6px 16px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.4);
    color: #dcebff;
    font-size: 13px;
    letter-spacing: 2px;
    margin-bottom: 18px;
}

.hero-title {
    margin: 0;
    font-size: 38px;
    font-weight: 800;
    letter-spacing: 1px;
}

.hero-sub {
    margin: 16px 0 0;
    font-size: 17px;
    line-height: 1.8;
    opacity: 0.92;
    font-weight: 500;
}

.hero-actions {
    margin-top: 34px;
    display: flex;
    gap: 14px;
}

.hero-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 46px;
    min-width: 140px;
    padding: 0 26px;
    border-radius: 999px;
    border: 1.5px solid rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.2s, transform 0.2s;
}

.hero-btn:hover {
    background: rgba(255, 255, 255, 0.26);
    transform: translateY(-2px);
}

.hero-btn.primary {
    background: linear-gradient(135deg, #ffc53d, #f5a623);
    color: #17324d;
    border-color: transparent;
    box-shadow: 0 10px 24px rgba(245, 166, 35, 0.35);
}

/* ===== Tabs ===== */
.tabs-wrap {
    position: sticky;
    z-index: 20;
    background: #fff;
    border-bottom: 1px solid #eee;
}

.container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
}

.tabs {
    display: flex;
    gap: 44px;
    height: 64px;
    align-items: center;
}

.tab {
    position: relative;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 10px 2px;
    font-size: 15px;
    color: #666;
}

.tab:hover {
    color: #0a4f97;
}

.tab.active {
    color: #0a4f97;
    font-weight: 800;
}

.tab.active::after {
    content: "";
    position: absolute;
    left: 0;
    width: 100%;
    bottom: -2px;
    height: 3px;
    border-radius: 3px;
    background: #0a4f97;
}

/* ===== Sections ===== */
.section {
    padding: 70px 0;
}

.section-title {
    margin: 0;
    text-align: center;
    font-size: 34px;
    font-weight: 800;
    color: #102a43;
    letter-spacing: 1px;
}

.section-sub {
    margin: 18px auto 0;
    text-align: center;
    max-width: 860px;
    font-size: 14px;
    color: #7b8a9c;
    line-height: 1.9;
}

/* ===== Pain Grid ===== */
.pain-grid {
    margin-top: 40px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 22px;
}

.pain-card {
    border: 1px solid #eef1f5;
    background: #fff;
    border-radius: 14px;
    overflow: hidden;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.pain-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 18px 40px rgba(16, 42, 67, 0.1);
}

.pain-img {
    position: relative;
    height: 170px;
    overflow: hidden;
}

.pain-img img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.img-ph {
    height: 100%;
    background: #f2f4f7;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
}

.pain-body {
    padding: 18px 18px 22px;
}

.pain-title {
    font-size: 18px;
    font-weight: 800;
    color: #102a43;
}

.pain-desc {
    margin-top: 12px;
    font-size: 14px;
    color: #7b8a9c;
    line-height: 1.9;
}

/* ===== Solution Cards ===== */
.section-solution {
    background: #f6f9fd;
}

.sol-grid {
    margin-top: 46px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 22px 26px;
}

.sol-card {
    border: 1px solid #eef1f5;
    border-radius: 14px;
    background: #fff;
    padding: 26px;
    display: flex;
    gap: 16px;
    align-items: flex-start;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.sol-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px rgba(10, 79, 151, 0.1);
}

.sol-ico {
    width: 48px;
    height: 48px;
    flex: 0 0 auto;
    border-radius: 12px;
    background: #e8f1fc;
    color: #0a4f97;
    display: flex;
    align-items: center;
    justify-content: center;
}

.sol-svg {
    width: 26px;
    height: 26px;
}

.sol-title {
    font-size: 18px;
    font-weight: 800;
    color: #102a43;
    margin-top: 2px;
}

.sol-desc {
    margin-top: 12px;
    font-size: 14px;
    color: #7b8a9c;
    line-height: 1.9;
}

/* ===== Case Grid ===== */
.section-case {
    padding-bottom: 90px;
}

.case-grid {
    margin-top: 44px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 26px;
}

.case-item {
    text-align: center;
}

.case-logo {
    height: 120px;
    border: 1px solid #eef1f5;
    background: linear-gradient(160deg, #f8fbff, #eef4fc);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.logo-ph {
    color: #0a4f97;
    font-weight: 800;
    font-size: 20px;
    letter-spacing: 2px;
}

.case-name {
    margin-top: 14px;
    font-size: 16px;
    font-weight: 800;
    color: #102a43;
}

.case-desc {
    margin-top: 8px;
    font-size: 13px;
    color: #7b8a9c;
    line-height: 1.7;
}

/* ===== Responsive ===== */
@media (max-width: 1100px) {
    .hero-title {
        font-size: 30px;
    }

    .pain-grid {
        grid-template-columns: 1fr;
    }

    .sol-grid {
        grid-template-columns: 1fr;
    }

    .case-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 520px) {
    .hero-inner {
        padding: 70px 18px 60px;
    }

    .tabs {
        gap: 24px;
        overflow-x: auto;
    }

    .case-grid {
        grid-template-columns: 1fr;
    }
}
</style>
